/* eslint-disable class-methods-use-this */
import React from 'react';
import PropTypes from 'prop-types';
import anaUtils from 'analytics/utils';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import localeUtils from 'utils/LanguageLocale';
import basketUtils from 'utils/Basket';
import orderUtils from 'utils/Order';
import { isSdkPlusEnabled } from 'utils/adUtils';

// Global launcher promise reused across the SPA for the legacy SDK path
let launcherPromise = null;

class RoktAdPlacement extends BaseClass {
    constructor(props) {
        super(props);

        const { identifier, accountId } = props;

        this.accountId = accountId;
        this.identifier = identifier;
        this.selection = null;
        this.placementShown = false;
        this.roktContainerRef = React.createRef();
    }

    shouldComponentUpdate(nextProps) {
        if (isSdkPlusEnabled()) {
            const hasOrderNow = !!nextProps.orderDetails?.header?.orderId;
            const hadOrderBefore = !!this.props.orderDetails?.header?.orderId;

            return hasOrderNow && !hadOrderBefore;
        }

        return false;
    }

    componentDidMount() {
        this._mounted = true;

        if (isSdkPlusEnabled()) {
            this.showSdkPlusPlacement();
        } else {
            this.initializeRoktAndShowPlacement().catch(err => {
                Sephora.logger.error('[Rokt] legacy init failed:', err);
            });
        }
    }

    componentDidUpdate() {
        if (isSdkPlusEnabled()) {
            this.showSdkPlusPlacement();
        }
    }

    componentWillUnmount() {
        this._mounted = false;

        if (this.selection) {
            this.selection.close();
        }
    }

    // --- Legacy SDK path ---

    initializeRoktAndShowPlacement = async () => {
        if (!launcherPromise) {
            launcherPromise = this.createLauncherPromise().catch(err => {
                launcherPromise = null;
                throw err;
            });
        }

        const launcher = await launcherPromise;
        await this.showLegacyPlacement(launcher);
    };

    createLauncherPromise = () => {
        return new Promise((resolve, reject) => {
            const createLauncher = () => {
                try {
                    resolve(
                        window.Rokt.createLauncher({
                            accountId: this.accountId,
                            sandbox: Sephora.UFE_ENV !== 'PROD'
                        })
                    );
                } catch (err) {
                    reject(err);
                }
            };

            if (window.Rokt) {
                createLauncher();

                return;
            }

            const existingScript = document.getElementById('rokt-launcher');

            if (existingScript) {
                existingScript.onload = createLauncher;
                existingScript.onerror = reject;

                return;
            }

            const target = document.head || document.body;
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = 'https://apps.rokt.com/wsdk/integrations/launcher.js';
            script.fetchPriority = 'high';
            script.crossOrigin = 'anonymous';
            script.async = true;
            script.id = 'rokt-launcher';
            script.onload = createLauncher;
            script.onerror = reject;

            target.appendChild(script);
        });
    };

    showLegacyPlacement = async launcher => {
        const { orderDetails = null } = this.props;
        const attributes = await this.buildLegacyAttributes(orderDetails);

        this.selection = await launcher.selectPlacements({
            identifier: this.identifier,
            attributes
        });
    };

    getLegacyCartItems = items => {
        return items.map(item => {
            const sku = item?.sku;
            const topCategory = this.getTopLevelCategory(sku);

            return {
                majorCat: topCategory?.displayName || '',
                sku: sku?.skuId,
                productName: sku?.productName,
                quantity: item?.qty,
                price: parseFloat(parseFloat(basketUtils.removeCurrency(sku?.salePrice || sku?.listPrice)).toFixed(2)) || 0
            };
        });
    };

    buildLegacyAttributes = async orderDetails => {
        const {
            header, shippingGroups, paymentGroups, priceInfo, items
        } = orderDetails;

        const shippingAddress = shippingGroups?.shippingGroupsEntries?.[0]?.shippingGroup?.address;
        const billingAddress = paymentGroups?.paymentGroupsEntries?.[0]?.paymentGroup?.address;
        const emailSource = (header.profile?.login || billingAddress?.email || shippingAddress?.email || '').toLowerCase().trim();
        const email = await anaUtils.hashString(emailSource);
        const firstname = header.profile?.firstName ?? billingAddress?.firstName;
        const lastname = header.profile?.lastName ?? billingAddress?.lastName;
        const billingzipcode = billingAddress?.postalCode || shippingAddress?.postalCode || '';
        const country = header.profile?.profileLocale;
        const language = header.profile?.language;
        const amount = parseFloat(Number(basketUtils.removeCurrency(priceInfo?.orderTotal))).toFixed(2);
        const paymentType = orderUtils.getPaymentMethodsString(orderDetails);
        const cartItems = JSON.stringify(this.getLegacyCartItems(items?.items ?? []));

        return {
            emailsha256: email,
            firstname,
            lastname,
            billingzipcode,
            country,
            language,
            amount,
            paymentType,
            cartItems
        };
    };

    // --- SDK+ path ---

    showSdkPlusPlacement = () => {
        if (this.placementShown) {
            return;
        }

        const { orderDetails } = this.props;

        if (!orderDetails?.header?.orderId) {
            return;
        }

        if (typeof window.mParticle?.ready !== 'function') {
            return;
        }

        this.placementShown = true;

        window.mParticle.ready(async () => {
            if (!this._mounted) {
                return;
            }

            try {
                const attributes = await this.buildSdkPlusAttributes(orderDetails);

                await this.identifyUser(attributes);

                if (!this._mounted) {
                    return;
                }

                const selection = await window.mParticle.Rokt.selectPlacements({
                    identifier: this.identifier,
                    attributes
                });

                if (!this._mounted) {
                    selection.close();

                    return;
                }

                this.selection = selection;
            } catch (err) {
                this.placementShown = false;
                Sephora.logger.error('[Rokt] selectPlacements failed:', err);
            }
        });
    };

    identifyUser = attributes => {
        return new Promise((resolve, reject) => {
            const identifyRequest = {
                userIdentities: {
                    // eslint-disable-next-line camelcase
                    email_sha256: attributes.email,
                    ...(attributes.customerid && { customerid: attributes.customerid })
                }
            };

            try {
                window.mParticle.Identity.identify(identifyRequest, result => {
                    const user = result.getUser();

                    if (user) {
                        user.setUserAttribute('firstname', attributes.firstname);
                        user.setUserAttribute('lastname', attributes.lastname);
                        user.setUserAttribute('billingzipcode', attributes.billingzipcode);
                        user.setUserAttribute('country', attributes.country);
                        user.setUserAttribute('language', attributes.language);
                        user.setUserAttribute('customertype', attributes.customertype);

                        if (attributes.loyaltytier) {
                            user.setUserAttribute('loyaltytier', attributes.loyaltytier);
                        }

                        if (attributes.loyaltyid) {
                            user.setUserAttribute('loyaltyid', attributes.loyaltyid);
                        }
                    } else {
                        Sephora.logger.warn('[Rokt] mParticle identity failed, proceeding without user attributes');
                    }

                    resolve();
                });
            } catch (err) {
                reject(err);
            }
        });
    };

    getTopLevelCategory = sku => {
        if (!sku?.parentCategory) {
            return null;
        }

        let current = sku.parentCategory;

        while (current.parentCategory) {
            current = current.parentCategory;
        }

        return current;
    };

    getCartItems = items => {
        return items.map(item => {
            const sku = item?.sku;
            const topCategory = this.getTopLevelCategory(sku);

            return {
                cartitemid: sku?.skuId,
                productsku: sku?.skuId,
                productname: sku?.productName,
                productcategory: topCategory?.displayName || '',
                productbrand: sku?.brandName || '',
                productvariant: sku?.variationValue || '',
                quantity: item?.qty,
                itemprice: parseFloat(parseFloat(basketUtils.removeCurrency(sku?.salePrice || sku?.listPrice)).toFixed(2)) || 0
            };
        });
    };

    getOrderEmailSource = (header, billingAddress, shippingAddress) => {
        const raw = header.isGuestOrder
            ? header.guestProfile?.email || billingAddress?.email || shippingAddress?.email
            : header.profile?.login || billingAddress?.email || shippingAddress?.email;

        return (raw || '').toLowerCase().trim();
    };

    buildSdkPlusAttributes = async orderDetails => {
        const {
            header, shippingGroups, paymentGroups, priceInfo, items
        } = orderDetails;

        const shippingAddress = shippingGroups?.shippingGroupsEntries?.[0]?.shippingGroup?.address;
        const billingAddress = paymentGroups?.paymentGroupsEntries?.[0]?.paymentGroup?.address;

        const emailSource = this.getOrderEmailSource(header, billingAddress, shippingAddress);
        const email = await anaUtils.hashString(emailSource);

        const biAccount = header?.profile?.beautyInsiderAccount;
        const loyaltyStatus = biAccount?.loyaltyTierName?.toLowerCase() || '';

        const nameSource = header.isGuestOrder ? billingAddress : header.profile;

        const attrs = {
            email,
            customerid: header.profile?.profileId || '',
            firstname: nameSource?.firstName ?? '',
            lastname: nameSource?.lastName ?? '',
            confirmationref: header?.orderId || '',
            totalprice: parseFloat(basketUtils.removeCurrency(priceInfo?.orderTotal)) || 0,
            amount: parseFloat(basketUtils.removeCurrency(priceInfo?.merchandiseSubtotal)) || 0,
            currency: 'USD',
            country: header.profile?.profileLocale || '',
            language: localeUtils.getCurrentLanguage().toLowerCase(),
            billingzipcode: billingAddress?.postalCode || shippingAddress?.postalCode || '',
            customertype: header?.isGuestOrder ? 'guest' : 'logged_in',
            paymenttype: orderUtils.getPaymentMethodsString(orderDetails),
            cartItems: this.getCartItems(items?.items ?? [])
        };

        if (loyaltyStatus) {
            attrs.loyaltytier = loyaltyStatus;
        }

        if (biAccount?.biAccountId) {
            attrs.loyaltyid = biAccount.biAccountId;
        }

        return attrs;
    };

    render() {
        return (
            <div
                id='rokt-container'
                ref={this.roktContainerRef}
            ></div>
        );
    }
}

RoktAdPlacement.propTypes = {
    orderDetails: PropTypes.shape({
        header: PropTypes.shape({
            orderId: PropTypes.string,
            orderLocale: PropTypes.string,
            isGuestOrder: PropTypes.bool,
            isRopisOrder: PropTypes.bool,
            isBopisOrder: PropTypes.bool,
            isSameDayOrder: PropTypes.bool,
            profile: PropTypes.shape({
                email: PropTypes.string,
                beautyInsiderAccount: PropTypes.shape({
                    biAccountId: PropTypes.string
                })
            }),
            guestProfile: PropTypes.shape({
                email: PropTypes.string
            })
        }),
        shippingGroups: PropTypes.shape({
            shippingGroupsEntries: PropTypes.arrayOf(
                PropTypes.shape({
                    shippingGroup: PropTypes.shape({
                        address: PropTypes.shape({
                            firstName: PropTypes.string,
                            lastName: PropTypes.string,
                            postalCode: PropTypes.string
                        })
                    })
                })
            )
        }),
        paymentGroups: PropTypes.shape({
            paymentGroupsEntries: PropTypes.arrayOf(
                PropTypes.shape({
                    paymentGroup: PropTypes.shape({
                        address: PropTypes.shape({
                            firstName: PropTypes.string,
                            lastName: PropTypes.string,
                            postalCode: PropTypes.string
                        })
                    })
                })
            )
        }),
        priceInfo: PropTypes.shape({
            orderTotal: PropTypes.string,
            merchandiseSubtotal: PropTypes.string
        }),
        items: PropTypes.shape({
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    sku: PropTypes.shape({
                        categoryName: PropTypes.string,
                        brandName: PropTypes.string
                    })
                })
            ),
            earnedPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            redeemedPoints: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            netBeautyBankPointsAvailable: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
    })
};

export default wrapComponent(RoktAdPlacement, 'RoktAdPlacement', true);
